import React, { useMemo } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { AnimationDirection } from "./types";

interface AnimatedEntranceProps {
  children: React.ReactNode;
  duration?: number;
  delay?: number;
  distance?: number;
  direction?: AnimationDirection;
  sx?: SxProps<Theme>;
  component?: React.ElementType;
}

const AnimatedEntrance: React.FC<AnimatedEntranceProps> = ({
  children,
  duration = 0.3,
  delay = 0,
  distance = 20,
  direction = "up",
  sx = {},
  component = "div",
}) => {
  const animationName = useMemo(
    () =>
      `fadeIn${direction.charAt(0).toUpperCase()}${direction.slice(1)}-${Math.random().toString(36).substr(2, 9)}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getTransform = (dir: AnimationDirection) => {
    switch (dir) {
      case "up":
        return `translateY(${distance}px)`;
      case "down":
        return `translateY(-${distance}px)`;
      case "left":
        return `translateX(${distance}px)`;
      case "right":
        return `translateX(-${distance}px)`;
      default:
        return `translateY(${distance}px)`;
    }
  };

  return (
    <Box
      component={component}
      sx={{
        // Initial state to prevent flash of visible content
        opacity: 0,
        visibility: "hidden",
        transform: getTransform(direction),
        // Animation properties
        animation: `${animationName} ${duration}s ease-in-out`,
        animationDelay: `${delay}s`,
        animationFillMode: "forwards", // Keep final state after animation ends
        [`@keyframes ${animationName}`]: {
          from: {
            visibility: "hidden",
            opacity: 0,
            transform: getTransform(direction),
          },
          to: {
            opacity: 1,
            transform: "translate(0)",
            visibility: "visible",
          },
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default AnimatedEntrance;
